import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import LoginOverlay from "./LoginOverlay";
import { Loader } from "./ds";

/**
 * ProtectedRoute component ensures:
 * 1. User is authenticated (shows login overlay if not)
 * 2. User has completed their profile (redirects to profile completion if not)
 */
const ProtectedRoute = ({ requireProfileCompletion = true }) => {
  const { isAuthenticated, loading, profileCompleted } = useAuth();
  const location = useLocation();
  const [showLogin, setShowLogin] = React.useState(false);

  if (loading) {
    // Show loading indicator while checking authentication state
    return (
      <div className="min-h-screen flex justify-center items-center bg-main-bg dark:bg-main-dark-bg">
        <Loader label="Authenticating…" />
      </div>
    );
  }

  // Show login overlay if not authenticated
  if (!isAuthenticated) {
    // If we're already on the landing page, just show the overlay
    if (location.pathname === "/") {
      return (
        <>
          <Outlet />
          <LoginOverlay
            isOpen={true}
            onClose={() => setShowLogin(false)}
            onLoginSuccess={() => setShowLogin(false)}
          />
        </>
      );
    }
    // Otherwise redirect to landing page with login overlay
    return (
      <Navigate to="/" state={{ showLogin: true, from: location }} replace />
    );
  }

  // If profile completion is required but profile is not complete, redirect to profile completion
  if (requireProfileCompletion && !profileCompleted) {
    // Don't redirect if we're already on profile completion page to avoid loops
    if (!location.pathname.includes("profile-completion")) {
      return <Navigate to="/profile-completion" replace />;
    }
  }

  // If all checks pass, render the child routes
  return <Outlet />;
};

export default ProtectedRoute;
